import {
  MotifChip,
  MotifFormField,
  MotifToggleSwitch,
  MotifIconButton,
  MotifIcon,
} from "@ey-xd/motif-react";
import {
  IconoirEditPencil,
  IconoirTrash,
  IconoirNavArrowUp,
  IconoirNavArrowDown,
} from "@ey-xd/motif-icon";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const SortableHeader = (props: any) => {
  const columnField = props.column.getColDef().field as string;
  const currentSortOrder =
    props.sortColumn === columnField ? props.sortOrder : null;

  return (
    <div
      className="custom-header admin-sort-header"
      onClick={() => props.onSortChange(columnField)}
    >
      <span>{props.column.getColDef().headerName}</span>
      <span className="ag-sort-indicator-container" data-ref="eSortIndicator">
        <span
          data-ref="eSortAsc"
          className={`ag-sort-indicator-icon admin-sort-indicator-icon ${currentSortOrder === "DESC" ? "ag-hidden" : ""} ${currentSortOrder === "ASC" ? "admin-sort-indicator-icon--active" : ""}`}
          aria-hidden="true"
        >
          <MotifIcon iconFunction={IconoirNavArrowUp} size="14" />
        </span>
        <span
          data-ref="eSortDesc"
          className={`ag-sort-indicator-icon admin-sort-indicator-icon ${currentSortOrder === "ASC" ? "ag-hidden" : ""} ${currentSortOrder === "DESC" ? "admin-sort-indicator-icon--active" : ""}`}
          aria-hidden="true"
        >
          <MotifIcon iconFunction={IconoirNavArrowDown} size="14" />
        </span>
      </span>
    </div>
  );
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const CenteredHeader = (props: any) => {
  const { column } = props;
  return (
    <div className="custom-header custom-header--centered">
      <span>{column.getColDef().headerName}</span>
    </div>
  );
};

export type PaginationType = {
  currentPage: number;
  pageSize: number;
  searchText: string;
  SortOrder: "ASC" | "DESC";
  SortColumn: string;
};

export type AdminSupportRow = Record<
  string,
  string | number | boolean | string[] | null | undefined
>;

const getRoleChipLabel = (roleCd: unknown): string => {
  const normalizedRoleCd = String(roleCd || "").toLowerCase();

  if (normalizedRoleCd === "system_admin") {
    return "Admin";
  }
  if (normalizedRoleCd === "system_support") {
    return "Support";
  }

  return "-";
};

const getRoleChipClassName = (roleCd: unknown): string => {
  const normalizedRoleCd = String(roleCd || "").toLowerCase();
  if (normalizedRoleCd === "system_admin") {
    return "admin-role-chip";
  }
  if (normalizedRoleCd === "system_support") {
    return "support-role-chip";
  }
  return "";
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const getAdminSupportTableColumns = (
  handleSortChange: (columnName: string) => void,
  paginationObj: PaginationType,
  handleEdit: (row: AdminSupportRow) => void,
  setDeleteConfirmationModalVisible: (isVisible: boolean) => void,
  setSelectedRowForDeletion: (row: AdminSupportRow | null) => void,
  emailId: string,
  handleToggleActive: (row: AdminSupportRow) => void,
): any[] => [
  {
    headerName: "Name",
    field: "Name",
    width: 372,
    minWidth: 372,
    flex: 3,
    headerComponent: SortableHeader,
    headerComponentParams: {
      onSortChange: handleSortChange,
      sortColumn: paginationObj.SortColumn,
      sortOrder: paginationObj.SortOrder,
    },
    cellRenderer: (params: any) => params?.data?.Name || "-",
  },
  {
    headerName: "Email",
    field: "Email",
    width: 372,
    minWidth: 372,
    flex: 3,
    headerComponent: SortableHeader,
    headerComponentParams: {
      onSortChange: handleSortChange,
      sortColumn: paginationObj.SortColumn,
      sortOrder: paginationObj.SortOrder,
    },
    cellRenderer: (params: any) => params?.data?.Email || "-",
  },
  {
    headerName: "Role",
    field: "RoleCd",
    width: 140,
    minWidth: 140,
    flex: 1.2,
    headerComponent: SortableHeader,
    headerComponentParams: {
      onSortChange: handleSortChange,
      sortColumn: paginationObj.SortColumn,
      sortOrder: paginationObj.SortOrder,
    },
    cellRenderer: (params: any) => {
      const roleCd = params?.data?.RoleCd;
      const roleLabel = getRoleChipLabel(roleCd);
      const roleChipClassName = getRoleChipClassName(roleCd);

      if (roleLabel === "-") {
        return "-";
      }

      return (
        <MotifChip className={roleChipClassName} title={roleLabel} type="chip">
          {roleLabel}
        </MotifChip>
      );
    },
  },
  {
    headerName: "Active",
    field: "IsActive",
    width: 140,
    minWidth: 140,
    flex: 1.2,
    headerComponent: SortableHeader,
    headerComponentParams: {
      onSortChange: handleSortChange,
      sortColumn: paginationObj.SortColumn,
      sortOrder: paginationObj.SortOrder,
    },
    cellRenderer: (params: any) => (
      <MotifFormField>
        <MotifToggleSwitch
          id={`admin-toggle-active-${params?.data?.Email || params?.rowIndex}`}
          checked={params?.data?.IsActive || false}
          onLabel="Yes"
          offLabel="No"
          onChange={() => {
            if (params?.data) {
              handleToggleActive(params.data);
            }
          }}
          disabled={params?.data?.Email === emailId}
        />
      </MotifFormField>
    ),
  },
  {
    headerName: "Actions",
    field: "Actions",
    width: 116,
    minWidth: 116,
    flex: 1,
    headerComponent: CenteredHeader,
    cellRenderer: (params: { data?: AdminSupportRow }) => (
      <div className="action-container">
        <MotifIconButton
          disabled={params?.data?.Email === emailId}
          onClick={() => {
            if (params.data) {
              handleEdit(params.data);
            }
          }}
          aria-label="Edit"
        >
          <MotifIcon
            size="20"
            iconFunction={IconoirEditPencil}
            className="action-icon"
          />
        </MotifIconButton>
        <MotifIconButton
          disabled={params?.data?.Email === emailId}
          onClick={() => {
            setSelectedRowForDeletion(params?.data || null);
            setDeleteConfirmationModalVisible(true);
          }}
          aria-label="Delete"
        >
          <MotifIcon
            size="20"
            iconFunction={IconoirTrash}
            className="action-icon"
          />
        </MotifIconButton>
      </div>
    ),
  },
];
